import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { prCampaignsAPI } from '../api/prCampaigns';
import {
  Plus,
  Newspaper,
  Trash2,
  Loader2,
  ChevronRight,
  X
} from 'lucide-react';

const PrCampaigns = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({ gameTitle: '', notes: '', startDate: '', endDate: '', includeKeywords: '', excludeKeywords: '' });
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await prCampaignsAPI.getAll();
      setCampaigns(response.data.campaigns);
    } catch (err) {
      console.error('Error fetching PR campaigns:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Auto-select first campaign
  useEffect(() => {
    if (!selectedCampaignId && campaigns.length > 0) {
      setSelectedCampaignId(campaigns[0].id);
    }
  }, [campaigns, selectedCampaignId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.gameTitle.trim()) return;

    try {
      setCreating(true);
      await prCampaignsAPI.create({
        gameTitle: formData.gameTitle,
        notes: formData.notes || null,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
        includeKeywords: formData.includeKeywords ? formData.includeKeywords.split(',').map(k => k.trim()).filter(Boolean) : [],
        excludeKeywords: formData.excludeKeywords ? formData.excludeKeywords.split(',').map(k => k.trim()).filter(Boolean) : [],
      });
      setFormData({ gameTitle: '', notes: '', startDate: '', endDate: '', includeKeywords: '', excludeKeywords: '' });
      setShowCreateForm(false);
      fetchCampaigns();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to create PR campaign';
      alert(errorMsg);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this PR campaign and all its articles?')) return;

    try {
      setDeletingId(id);
      await prCampaignsAPI.delete(id);
      if (selectedCampaignId === id) setSelectedCampaignId(null);
      fetchCampaigns();
    } catch (err) {
      alert('Failed to delete PR campaign');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatRelative = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  const accentColors = ['#ec4899', '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#06b6d4'];
  const selectedCampaign = campaigns.find(c => c.id === selectedCampaignId) || null;
  const selectedAccent = selectedCampaign ? accentColors[campaigns.indexOf(selectedCampaign) % accentColors.length] : '#ec4899';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a14 50%, #0a0a1a 100%)' }}>
        <div className="flex flex-col items-center">
          <Loader2 size={32} className="animate-spin text-sc-pink" />
          <span className="text-white mt-4 font-medium">Loading PR campaigns...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a14 50%, #0a0a1a 100%)' }}>
      {/* Sidebar */}
      <div className="w-72 flex-shrink-0 flex flex-col">
        <div className="p-5 pb-4">
          <h1 className="text-xl font-bold text-white mb-2">PR Campaigns</h1>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[10px] px-2 py-0.5 rounded-md bg-white/15 text-white border border-white">
              {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''}
            </span>
            {campaigns.reduce((sum, c) => sum + (c.articleCount || 0), 0) > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-md bg-sc-pink/10 text-sc-pink/70 border border-sc-pink/15">
                {campaigns.reduce((sum, c) => sum + (c.articleCount || 0), 0)} articles
              </span>
            )}
          </div>
        </div>

        {campaigns.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <Newspaper className="w-10 h-10 text-white/10 mx-auto mb-3" />
            <p className="text-xs text-white">No PR campaigns yet</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
            {campaigns.map((campaign, idx) => {
              const isSelected = campaign.id === selectedCampaignId;
              const accent = accentColors[idx % accentColors.length];

              return (
                <button
                  key={campaign.id}
                  onClick={() => setSelectedCampaignId(campaign.id)}
                  className={`w-full text-left px-3 py-3 rounded-xl transition-all duration-200 group relative overflow-hidden ${
                    isSelected
                      ? 'bg-white/[0.07] border border-white'
                      : 'hover:bg-white/[0.03] border border-transparent'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute -top-8 -right-8 w-20 h-20 rounded-full opacity-15 blur-2xl" style={{ background: accent }} />
                  )}
                  <div className="relative z-10">
                    <div className="flex items-center gap-2.5 mb-1.5">
                      {campaign.gameImage ? (
                        <img src={campaign.gameImage} alt="" className="w-7 h-7 rounded-md object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-7 h-7 rounded-md flex-shrink-0 flex items-center justify-center" style={{ background: `${accent}15` }}>
                          <Newspaper className="w-3.5 h-3.5" style={{ color: accent }} />
                        </div>
                      )}
                      <span className={`text-sm font-medium truncate ${isSelected ? 'text-white' : 'text-white group-hover:text-white'}`}>
                        {campaign.gameTitle}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 ml-[38px]">
                      <span className="text-[10px] text-sc-pink/60">{campaign.articleCount} article{campaign.articleCount !== 1 ? 's' : ''}</span>
                      <span className="text-[10px] text-white/20">checked {formatRelative(campaign.lastCheckedAt)}</span>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      <ChevronRight size={14} className="text-white/20" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {selectedCampaign ? (
          <>
            {/* Campaign Header */}
            <div className="p-6 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {selectedCampaign.gameImage ? (
                    <img src={selectedCampaign.gameImage} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                  ) : (
                    <div className="p-2.5 rounded-xl relative overflow-hidden" style={{ background: `${selectedAccent}15` }}>
                      <div className="absolute inset-0 opacity-20 blur-sm" style={{ background: selectedAccent }} />
                      <Newspaper size={20} style={{ color: selectedAccent }} className="relative z-10" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-lg font-bold text-white">{selectedCampaign.gameTitle}</h2>
                    <p className="text-xs text-white">
                      {selectedCampaign.articleCount} article{selectedCampaign.articleCount !== 1 ? 's' : ''}
                      {selectedCampaign.startDate && selectedCampaign.endDate && (
                        <> · {formatDate(selectedCampaign.startDate)} - {formatDate(selectedCampaign.endDate)}</>
                      )}
                      {selectedCampaign.lastCheckedAt && (
                        <> · Last checked {formatRelative(selectedCampaign.lastCheckedAt)}</>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {selectedCampaign.articleCount > 0 && (
                    <span className="text-[11px] bg-sc-pink/15 text-sc-pink border border-sc-pink/20 px-2.5 py-1 rounded-lg">
                      {selectedCampaign.articleCount} Articles
                    </span>
                  )}
                  <button
                    onClick={() => navigate(`/dashboard/pr-campaigns/${selectedCampaign.id}`)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 transition-all text-xs font-semibold"
                  >
                    View Campaign
                  </button>
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 transition-all text-xs font-semibold"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    New Campaign
                  </button>
                </div>
              </div>
            </div>

            {/* Campaign Details */}
            <div className="flex-1 overflow-y-auto p-6 pt-4">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-900/80 border border-white rounded-xl p-4 relative overflow-hidden">
                  <div className="absolute -top-10 -right-10 w-24 h-24 rounded-full opacity-20 blur-2xl" style={{ background: '#ec4899' }} />
                  <p className="text-xs text-white relative z-10 mb-1">Articles</p>
                  <p className="text-2xl font-bold text-white relative z-10">{selectedCampaign.articleCount}</p>
                </div>
                <div className="bg-gray-900/80 border border-white rounded-xl p-4 relative overflow-hidden">
                  <div className="absolute -top-10 -right-10 w-24 h-24 rounded-full opacity-20 blur-2xl" style={{ background: '#3b82f6' }} />
                  <p className="text-xs text-white relative z-10 mb-1">Created</p>
                  <p className="text-lg font-bold text-white relative z-10">{formatDate(selectedCampaign.createdAt)}</p>
                </div>
                <div className="bg-gray-900/80 border border-white rounded-xl p-4 relative overflow-hidden">
                  <div className="absolute -top-10 -right-10 w-24 h-24 rounded-full opacity-20 blur-2xl" style={{ background: '#10b981' }} />
                  <p className="text-xs text-white relative z-10 mb-1">Last Checked</p>
                  <p className="text-lg font-bold text-white relative z-10">{formatRelative(selectedCampaign.lastCheckedAt)}</p>
                </div>
                <div className="bg-gray-900/80 border border-white rounded-xl p-4 relative overflow-hidden">
                  <div className="absolute -top-10 -right-10 w-24 h-24 rounded-full opacity-20 blur-2xl" style={{ background: '#a855f7' }} />
                  <p className="text-xs text-white relative z-10 mb-1">Period</p>
                  <p className="text-sm font-bold text-white relative z-10">
                    {selectedCampaign.startDate && selectedCampaign.endDate
                      ? `${formatDate(selectedCampaign.startDate)} - ${formatDate(selectedCampaign.endDate)}`
                      : selectedCampaign.startDate
                      ? `From ${formatDate(selectedCampaign.startDate)}`
                      : selectedCampaign.endDate
                      ? `Until ${formatDate(selectedCampaign.endDate)}`
                      : 'All time'}
                  </p>
                </div>
              </div>

              {/* Keywords */}
              {(selectedCampaign.includeKeywords?.length > 0 || selectedCampaign.excludeKeywords?.length > 0) && (
                <div className="bg-gray-900/80 border border-white rounded-xl p-5 relative overflow-hidden mb-4">
                  <div className="absolute -top-10 -left-10 w-32 h-32 rounded-full opacity-10 blur-2xl" style={{ background: '#6366f1' }} />
                  <h3 className="text-sm font-semibold text-white mb-3 relative z-10">Keywords</h3>
                  <div className="flex flex-wrap gap-1.5 relative z-10">
                    {selectedCampaign.includeKeywords?.map((kw, i) => (
                      <span key={`inc-${i}`} className="text-xs bg-green-500/15 text-green-400 border border-green-500/20 px-2 py-0.5 rounded-lg">+{kw}</span>
                    ))}
                    {selectedCampaign.excludeKeywords?.map((kw, i) => (
                      <span key={`exc-${i}`} className="text-xs bg-red-500/15 text-red-400 border border-red-500/20 px-2 py-0.5 rounded-lg">-{kw}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedCampaign.notes && (
                <div className="bg-gray-900/80 border border-white rounded-xl p-5 relative overflow-hidden mb-4">
                  <h3 className="text-sm font-semibold text-white mb-2">Notes</h3>
                  <p className="text-sm text-white">{selectedCampaign.notes}</p>
                </div>
              )}

              {/* Actions */}
              <div className="bg-gray-900/80 border border-white rounded-xl p-5 relative overflow-hidden">
                <h3 className="text-sm font-semibold text-white mb-3">Actions</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(`/dashboard/pr-campaigns/${selectedCampaign.id}`)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 transition-all text-sm font-medium"
                  >
                    <Newspaper className="w-4 h-4" />
                    View Full Campaign
                  </button>
                  <button
                    onClick={(e) => handleDelete(e, selectedCampaign.id)}
                    disabled={deletingId === selectedCampaign.id}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400/70 hover:text-red-400 hover:bg-red-500/20 transition-all text-sm font-medium"
                  >
                    {deletingId === selectedCampaign.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Newspaper className="w-16 h-16 text-white/10 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No PR campaigns yet</h3>
              <p className="text-white mb-6 text-sm">Create a PR campaign to start tracking press coverage.</p>
              <button
                onClick={() => setShowCreateForm(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 transition-all text-sm font-semibold"
              >
                <Plus className="w-4 h-4" />
                Create Your First PR Campaign
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#241d33] border border-white rounded-xl p-6 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">New PR Campaign</h2>
              <button
                onClick={() => { setShowCreateForm(false); setFormData({ gameTitle: '', notes: '', startDate: '', endDate: '', includeKeywords: '', excludeKeywords: '' }); }}
                className="text-white hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-white mb-1">Game Title *</label>
                <input
                  type="text"
                  value={formData.gameTitle}
                  onChange={(e) => setFormData({ ...formData, gameTitle: e.target.value })}
                  placeholder="e.g. High on Life 2"
                  className="w-full px-3 py-2 border border-white rounded-lg text-sm text-white bg-white/5 focus:outline-none focus:border-sc-pink/40"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-white mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional notes..."
                  rows={2}
                  className="w-full px-3 py-2 border border-white rounded-lg text-sm text-white bg-white/5 focus:outline-none focus:border-sc-pink/40"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-white mb-1">Start Date</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-white rounded-lg text-sm text-white bg-white/5 focus:outline-none focus:border-sc-pink/40"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-white mb-1">End Date</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-white rounded-lg text-sm text-white bg-white/5 focus:outline-none focus:border-sc-pink/40"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-white mb-1">Include Keywords</label>
                  <input
                    type="text"
                    value={formData.includeKeywords}
                    onChange={(e) => setFormData({ ...formData, includeKeywords: e.target.value })}
                    placeholder="review, preview"
                    className="w-full px-3 py-2 border border-white rounded-lg text-sm text-white bg-white/5 focus:outline-none focus:border-sc-pink/40"
                  />
                  <p className="text-[10px] text-white mt-1">Comma-separated</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-white mb-1">Exclude Keywords</label>
                  <input
                    type="text"
                    value={formData.excludeKeywords}
                    onChange={(e) => setFormData({ ...formData, excludeKeywords: e.target.value })}
                    placeholder="walkthrough, guide"
                    className="w-full px-3 py-2 border border-white rounded-lg text-sm text-white bg-white/5 focus:outline-none focus:border-sc-pink/40"
                  />
                  <p className="text-[10px] text-white mt-1">Comma-separated</p>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowCreateForm(false); setFormData({ gameTitle: '', notes: '', startDate: '', endDate: '', includeKeywords: '', excludeKeywords: '' }); }}
                  className="flex-1 px-4 py-2 text-white bg-white/5 hover:bg-white/10 border border-white rounded-lg text-sm font-medium transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !formData.gameTitle.trim()}
                  className="flex-1 px-4 py-2 bg-sc-pink hover:bg-sc-pink/90 text-white rounded-lg text-sm font-medium transition disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  {creating && <Loader2 size={14} className="animate-spin" />}
                  {creating ? 'Creating...' : 'Create Campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrCampaigns;
